import { useState, useMemo } from 'react';
import {
    IconBuildingBank,
    IconSearch,
    IconLayoutGrid,
    IconRosetteDiscountCheckFilled,
    IconExternalLink,
} from '@tabler/icons-react';
import { toBnDigits } from '../../../utils/bnFormat';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import data from '../../../data/sorkariServices.json';

const SERVICES = data.services;

// Category tabs (order fixed); "all" is prepended in the render.
const CATEGORY_TABS = [
    { key: 'sorkari', labelKey: 'sorkariLinks.cat.sorkari' },
    { key: 'educational', labelKey: 'sorkariLinks.cat.educational' },
    { key: 'visa', labelKey: 'sorkariLinks.cat.visa' },
    { key: 'others', labelKey: 'sorkariLinks.cat.others' },
];

export default function SorkariLinks() {
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';
    const bn = (n) => (isBn ? toBnDigits(n) : String(n));

    const [query, setQuery] = useState('');
    const [activeCat, setActiveCat] = useState('all');

    // Per-category counts for the filter-chip badges.
    const counts = useMemo(() => {
        const c = { all: SERVICES.length };
        for (const s of SERVICES) c[s.category] = (c[s.category] || 0) + 1;
        return c;
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return SERVICES.filter((s) => {
            if (activeCat !== 'all' && s.category !== activeCat) return false;
            if (!q) return true;
            return [s.title, s.description, ...(s.tags || [])]
                .some((v) => (v || '').toLowerCase().includes(q));
        });
    }, [query, activeCat]);

    return (
        <div className="w-full font-hind py-2 text-zinc-900 sm:py-4 dark:text-zinc-100">
            {/* ── Header ── */}
            <div className="mb-[22px] flex flex-wrap items-end justify-between gap-[18px]">
                <div className="flex items-center gap-[11px]">
                    <div className="flex h-[42px] w-[42px] flex-none items-center justify-center rounded-xl bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900">
                        <IconBuildingBank size={22} stroke={1.6} />
                    </div>
                    <div>
                        <div className="text-[25px] font-bold tracking-[-0.01em] text-zinc-900 dark:text-zinc-100">
                            {t('sorkariLinks.title')}
                        </div>
                        <div className="mt-0.5 text-[13.5px] text-zinc-500 dark:text-zinc-400">
                            {t('sorkariLinks.subtitle')}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 rounded-[10px] border border-zinc-200 bg-white px-[13px] py-2 text-[12.5px] text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400">
                    <IconLayoutGrid size={15} className="text-primary" />
                    <span><b className="text-zinc-900 dark:text-zinc-100">{bn(SERVICES.length)}</b> {t('sorkariLinks.unit')}</span>
                </div>
            </div>

            {/* ── Search ── */}
            <div className="relative mb-4">
                <IconSearch size={19} stroke={1.8} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-500" />
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('sorkariLinks.searchPlaceholder')}
                    className="w-full rounded-[13px] border border-zinc-200 bg-white py-[14px] pl-[46px] pr-4 text-[14.5px] text-zinc-900 outline-none placeholder:text-zinc-400 focus:border-primary focus:ring-2 focus:ring-primary/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                />
            </div>

            {/* ── Category tabs ── */}
            <div className="mb-5 flex flex-nowrap items-center gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:gap-[9px] [scrollbar-width:none] [-ms-overflow-style:none]">
                {[{ key: 'all', labelKey: 'sorkariLinks.cat.all' }, ...CATEGORY_TABS].map((tab) => {
                    const active = activeCat === tab.key;
                    return (
                        <button
                            key={tab.key}
                            onClick={() => setActiveCat(tab.key)}
                            className={`flex items-center gap-2 whitespace-nowrap rounded-[11px] border px-[15px] py-[9px] text-[13.5px] transition-colors ${
                                active
                                    ? 'border-zinc-900 bg-zinc-900 font-semibold text-white dark:border-zinc-100 dark:bg-zinc-100 dark:text-zinc-900'
                                    : 'border-zinc-200 bg-white font-medium text-zinc-600 hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300'
                            }`}
                        >
                            {t(tab.labelKey)}
                            <span className={`rounded-[20px] px-[7px] py-px text-[11px] font-bold ${
                                active
                                    ? 'bg-white/[0.16] text-white dark:bg-zinc-900/20 dark:text-zinc-900'
                                    : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400'
                            }`}>
                                {bn(counts[tab.key] || 0)}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* ── Grid ── */}
            {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-[14px] border border-dashed border-zinc-200 py-20 text-center dark:border-zinc-700">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-zinc-100 text-zinc-400 dark:bg-zinc-800">
                        <IconBuildingBank size={26} stroke={1.6} />
                    </div>
                    <p className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">{t('sorkariLinks.empty')}</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 xl:grid-cols-5">
                    {filtered.map((s) => {
                        const Wrapper = s.url ? 'a' : 'div';
                        const linkProps = s.url ? { href: s.url, target: '_blank', rel: 'noopener noreferrer' } : {};
                        return (
                            <Wrapper
                                key={s.id}
                                {...linkProps}
                                className={`group relative flex flex-col items-center rounded-2xl border border-zinc-200 bg-white p-4 text-center no-underline transition-all dark:border-zinc-700 dark:bg-zinc-900 sm:p-5 ${
                                    s.url
                                        ? 'cursor-pointer hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md'
                                        : 'cursor-default opacity-70'
                                }`}
                            >
                                {s.verified !== false && (
                                    <IconRosetteDiscountCheckFilled
                                        size={20}
                                        className="absolute right-2.5 top-2.5 text-emerald-500"
                                    />
                                )}
                                <img src={s.icon} alt="" loading="lazy" className="h-[88px] w-[88px] flex-none object-contain" draggable="false" />
                                <div className="mt-3.5 flex items-center gap-1 font-semibold leading-snug text-zinc-800 group-hover:text-primary dark:text-zinc-100">
                                    <span className="text-[16px]">{s.title}</span>
                                    {s.url && <IconExternalLink size={14} className="mt-0.5 flex-none opacity-0 transition-opacity group-hover:opacity-60" />}
                                </div>
                                {s.description && (
                                    <div className="mt-1 text-[12.5px] leading-snug text-zinc-500 dark:text-zinc-400">
                                        {s.description}
                                    </div>
                                )}
                            </Wrapper>
                        );
                    })}
                </div>
            )}

            {/* ── Disclaimer ── */}
            <div className="relative mx-auto mt-8 w-full overflow-hidden rounded-2xl border border-amber-100 bg-amber-50/70 px-5 py-7 text-center dark:border-amber-900/30 dark:bg-amber-900/10 sm:px-10 sm:py-9 lg:max-w-[70%]">
                <span
                    aria-hidden="true"
                    className="pointer-events-none absolute -right-5 -top-5 flex h-20 w-20 items-center justify-center rounded-full bg-amber-100/70 text-4xl font-black text-amber-300 dark:bg-amber-900/20 dark:text-amber-700/70"
                >
                    !
                </span>
                <h2 className="relative text-[17px] font-bold text-primary sm:text-lg">
                    {t('sorkariLinks.disclaimerTitle')}
                </h2>
                <p className="relative mx-auto mt-3 max-w-3xl text-[13.5px] leading-relaxed text-zinc-600 dark:text-zinc-300">
                    {t('sorkariLinks.disclaimerBody')}
                </p>
            </div>
        </div>
    );
}
